import { useEffect, useState } from "react";
import { Box, Card, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { api } from "../api.js";
import ThalamosLogo from "../assets/img/thalamos_logo.svg";
import { CodeInputGrid } from "./eMhFormsShareCodePage.js";
import {
  Banner,
  BannerList,
  Button,
} from "~/components/design-system/index.js";
import { useScreenDetection } from "~/hooks/ScreenDetection/useScreenDetection.js";

export function GuestUserLoginPage({}: {}) {
  const { t } = useTranslation();
  const query = new URLSearchParams(window.location.search);
  const userId = query.get("userId");
  const returnTo = query.get("returnTo");
  const theme = useTheme();

  const [email, setEmail] = useState("");
  const [codeLoading, setCodeLoading] = useState(false);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [isSubmittingCode, setIsSubmittingCode] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [codeResent, setCodeResent] = useState(false);
  const { isMobileView } = useScreenDetection();

  const navigate = useNavigate();

  async function requestCode(forceSend: boolean) {
    setCodeLoading(true);
    const result = await api.users.requestGuestUserLoginCode(
      userId!,
      forceSend,
    );
    setCodeLoading(false);

    if (result.status === 200) {
      setEmail(result.data.email);
    } else if (result.status === 429) {
      setCodeError((result.data as any).reason);
    } else {
      setCodeError(t("pages.guestUserLoginPage.invalidLink"));
    }
  }

  const handleSubmitCode = async () => {
    if (code?.length !== 6 || isSubmittingCode) return;
    setIsSubmittingCode(true);
    setSubmissionError(null);

    try {
      if (!code) {
        setSubmissionError(t("pages.guestUserLoginPage.codeRequired"));
        return;
      }
      const result = await api.users.tryGuestUserLoginCode(userId!, code);
      const reason = (result.data as any).reason;

      if (result.status === 204) {
        navigate(returnTo || "/");
      } else {
        setSubmissionError(
          reason || t("pages.guestUserLoginPage.unknownError"),
        );
      }
    } catch {
      setSubmissionError(t("pages.guestUserLoginPage.unknownError"));
    } finally {
      setIsSubmittingCode(false);
    }
  };

  useEffect(() => {
    requestCode(false);
  }, []);

  return (
    <Box
      sx={{
        backgroundColor: "white",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        px: 5,
      }}
    >
      {!codeLoading && (
        <Card
          elevation={0}
          sx={{
            p: 4,
            maxWidth: 550,
            width: "100%",
            textAlign: "center",
            borderRadius: "12px",
            backgroundColor: theme.palette.background.lightGray,
          }}
        >
          <Box
            sx={{
              mb: 2,
            }}
          >
            <img
              src={ThalamosLogo}
              alt="Thalamos Logo"
              style={{
                height: "100px",
                verticalAlign: "middle",
                marginLeft: "4px",
              }}
            />
          </Box>

          {email && (
            <>
              <Typography variant="body2" sx={{ mb: 1.5, textAlign: "left" }}>
                {t("pages.guestUserLoginPage.emailSent")}
                <span style={{ fontWeight: 500 }}>{email}</span>.
              </Typography>
              <Typography variant="body2" sx={{ mb: 3, textAlign: "left" }}>
                {t("pages.guestUserLoginPage.expiryNotice")}
              </Typography>
            </>
          )}

          {codeError && (
            <Banner title={codeError} bannerType={BannerList.ERROR} />
          )}

          <Typography
            variant="body2"
            sx={{ fontWeight: 600, mb: 1, textAlign: "left" }}
          >
            {t("pages.guestUserLoginPage.enterCodeLabel")}
          </Typography>

          <CodeInputGrid
            code={code}
            setCode={setCode}
            isMobileView={isMobileView}
            fields={6}
            type="guest"
            onSubmit={() => {
              void handleSubmitCode();
            }}
          />

          {submissionError && (
            <Box sx={{ mb: 2 }}>
              <Banner
                bannerType={BannerList.ERROR}
                title={submissionError}
                dense
              />
            </Box>
          )}

          <Button
            label={t("buttonLabels.submit")}
            fullWidth
            size="large"
            disabled={code?.length !== 6 || isSubmittingCode}
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onClick={handleSubmitCode}
            sx={{
              fontWeight: "bold",
              mt: 1,
              boxShadow: "0px 2px 4px rgba(0,0,0,0.15)",
              textTransform: "uppercase",
            }}
          />

          <Box sx={{ mt: 3 }}>
            <Button
              variant="text"
              sx={{
                fontWeight: 600,
                textTransform: "none",
                padding: 0,
                minWidth: "auto",
              }}
              onClick={async () => {
                if (!codeResent) {
                  await requestCode(true);
                  setCodeResent(true);
                }
              }}
              disabled={codeResent}
              label={
                codeResent
                  ? t("pages.guestUserLoginPage.codeSent")
                  : t("pages.guestUserLoginPage.resendCode")
              }
            />
          </Box>

          <Typography
            variant="caption"
            align="center"
            sx={{
              mt: 4,
              display: "block",
            }}
          >
            {t("pages.shareCodePage.accessingCodeLinkLabel")}{" "}
            <Link
              target="_blank"
              rel="noopener noreferrer"
              to="https://thalamos1.zohodesk.eu/portal/en/kb/articles/guest-user-login"
            >
              {t("common.clickHere")}
            </Link>
          </Typography>
        </Card>
      )}
    </Box>
  );
}
